import { compose } from 'ramda';
/**
 * @name flattenArray
 * @param {Array} arrWithChildren
 * @description Recursively loops through nested
 * children arrays and outputs a flat array of their contents
 */
export const flattenArray = (arrayWithChildren = []) =>
  arrayWithChildren.reduce((arr, item) => {
    return [...arr, item, ...flattenArray(item.children)];
  }, []);

/**
 * @name removeNestedChildren
 * @param {Array} arrWithChildren
 * @description Clears nested children from
 * comments in an array. Used after flattening to avoid
 * duplicate children in the data layer.
 */
export const removeNestedChildren = (arrayWithChildren) => {
  return arrayWithChildren.map((item) => ({
    ...item,
    children: [],
    descendant_ids: []
  }));
};

/**
 * @name flattenChildrenInComments
 * @param {Array} arrWithChildren
 * @description Takes an array of comment objects
 * that have nested children and flattens them.
 * Before:              After:
 * parent               parent
 *  ↳child               ↳child
 *    ↳child             ↳child
 *      ↳child           ↳child
 */

export const flattenChildrenInComments = (comments = []) =>
  comments.map((item) => ({
    ...item,
    children: compose(removeNestedChildren, flattenArray)(item.children)
  }));

/**
 * @name flattenChildInComment
 * @description Takes a comment object
 * that have nested children and flattens them.
 * Before:              After:
 * parent               parent
 *  ↳child               ↳child
 *    ↳child             ↳child
 *      ↳child           ↳child
 */

export const flattenChildInComment = (
  parentComment = {},
  childComments = []
) => ({
  ...parentComment,
  children: compose(removeNestedChildren, flattenArray)(childComments)
});

/**
 * This function sorts a given (comments) array based on their created_at_integer property.
 * The created_at_integer property is an integer representation of  the time the comment was created in the application,
 * The higher the number the more recent the comment.
 * Items without a created_at_integer property will not be sorted.
 * Internally it uses array.sort function so WILL mutate the comments argument passed to it.
 * @param {Array.<Object>} comments An array of comment objects to sort.
 * @param {Boolean} oldestToNewest Whether the comments are sorted oldest to newest (true) or newest to oldest (false)
 * @returns {Array.<Object>} The comments array sorted from newest to oldest.
 */
export const sortComments = (comments, oldestToNewest = true) => {
  return comments.sort((a, b) => {
    if (
      Object.prototype.hasOwnProperty.call(a, 'created_at_integer') &&
      Object.prototype.hasOwnProperty.call(b, 'created_at_integer')
    ) {
      return oldestToNewest
        ? a.created_at_integer - b.created_at_integer
        : b.created_at_integer - a.created_at_integer;
    }
    return 0;
  });
};

export const addStatusProps = (comments, statusProps) =>
  comments.map(({ children = [], ...comment }) => {
    return {
      ...comment,
      children: addStatusProps(children, statusProps),
      ...statusProps
    };
  });

/** This function is mapping the parent comments adding loading and error state when the child comments are being fetched. As well as the commentWithChildren.
 *
 * @param {Array} comments: array with all the parent comments for a content
 * @param {Number} parentId: id of the parent to whom all the comments belong to.
 * @param {Object} childLoading: that keeps loading and error states.
 * @param {Object} commentWithChildren: when this helper function is called by the fetchChildCommentsSuccess, it will have this argument that will replace the existing comment.
 *
 * @endpoint: GET /contents/{id}/comments/{comment_id}/descendants
 */
export const updateChildLoadingPropsInComment = (
  comments = [],
  parentId,
  childLoading,
  commentWithChildren
) => {
  return comments.map((comment) => {
    if (comment.id === parentId) {
      const fetchCancelOrSuccessComment =
        commentWithChildren === undefined ? comment : commentWithChildren;
      return {
        ...fetchCancelOrSuccessComment,
        childLoading
      };
    }

    return comment;
  });
};

/**
 * This function helps us to find find a parent comment and flatten the children comments in case the API is returning them nested!!
 *
 * @param {Array} comments: all the parent comments for a content
 * @param {Number} parentId: id of the parent to whom all the comments belong to.
 * @param {Array} childComments: all the children comments from a given parentId (receive from the API)
 */
export const childLoadingComments = (
  comments = [],
  parentId,
  childComments
) => {
  const parentComment = comments.find((comment) => comment.id === parentId);
  if (!parentComment) return null;

  return flattenChildInComment(parentComment, childComments);
};

/**
 * This function has the purpose of returning an array of parentId's that have NO children given an array of comments
 *
 * @param {Array} comments: all the comments for a given content
 */
export const getParentsWithoutLoadedChildren = (comments = []) => {
  return comments
    .filter((comment) => {
      return comment.descendant_ids && comment.descendant_ids.length !== 0;
    })
    .map((filteredComment) => {
      return filteredComment.id;
    });
};

/**
 *  This function gets the oldest comment of a list and retrieves the creation date
 *
 * @param {Array} comments: all the comments for a given content
 */

export const getOldestCommentDate = (comments = []) => {
  if (comments && comments.length > 0) {
    const sortedComments = sortComments(comments, false);
    return sortedComments[comments.length - 1].created_at_integer;
  }
  return null;
};

/**
 * Reduces an array of community objects into a single object of objects where the
 * key for each object is the community id.
 * @param {array} array of communities objects
 * @returns object of community object where the key is the community id
 */
export const normaliseCommunities = (communities) => {
  return communities.reduce((prevValue, currentValue) => {
    return {
      ...prevValue,
      [currentValue.id]: currentValue
    };
  }, {});
};
